import React, { useState, useEffect } from 'react';
import { Chip, Tooltip } from '@mui/material';
import { ViiluDataGrid, PageHeader, PageContent, useRouter } from '@viilutalous/common';
import { DeliveryDetails, getDeliveryErrorMessage } from '@viilutalous/invoicing';
import { sendLogAPI } from '../sendLogAPI';
import { useTranslation } from 'react-i18next';

const METHOD_COLORS = {
  email: 'info',
  einvoice: 'primary',
  letter: 'secondary',
};

const STATUS_COLORS = {
  in_queue: 'warning',
  sent: 'info',
  processed: 'info',
  deferred: 'warning',
  delivered: 'success',
  bounced: 'error',
  blocked: 'error',
  dropped: 'error',
  spam: 'error',
  failed: 'error',
};

export default function SendingService({ tenantId }) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const methodLabel = (method, invoice) => {
    if (method !== 'einvoice') return t(`sending_service.methods.${method}`, method);
    // Use the saved invoice recipient, including business IDs without a company name.
    const company = invoice?.c_company?.trim() || invoice?.c_business_id?.trim();
    const kind = company ? 'einvoice' : invoice?.c_payer_name?.trim() ? 'einvoice_person' : 'einvoice_combined';
    return t(`invoices.sending_method.${kind}`);
  };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenantId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchData = async () => {
    if (!tenantId) return;
    try {
      const response = await sendLogAPI.getAll(tenantId);
      setRows(response.data.items || []);
    } catch (error) {
      console.error('Error fetching send log:', error);
    } finally {
      setLoading(false);
    }
  };

  const columnDefs = [
    {
      field: 'sent_at',
      headerName: t('sending_service.columns.sent_at'),
      width: 160,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString('fi-FI', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
      },
      sort: 'desc',
    },
    {
      field: 'document_type',
      headerName: t('sending_service.columns.type'),
      width: 120,
      cellRenderer: (params) => {
        const label = t(`sending_service.document_types.${params.value}`, params.value);
        return React.createElement(Chip, { label, size: 'small', variant: 'outlined' });
      },
    },
    {
      field: 'document_number',
      headerName: t('sending_service.columns.document_number'),
      width: 120,
    },
    {
      field: 'method',
      headerName: t('sending_service.columns.method'),
      width: 220,
      cellRenderer: (params) => {
        const label = methodLabel(params.value, params.data?.invoice_recipient);
        const color = METHOD_COLORS[params.value] || 'default';
        return React.createElement(Chip, { label, size: 'small', color });
      },
    },
    {
      field: 'confirmed_delivery_method',
      headerName: t('invoices.delivery_details.actual'),
      headerTooltip: t('invoices.delivery_details.log_hint'),
      width: 220,
      cellRenderer: (params) => params.data?.delivery_confirmation ? (
        <DeliveryDetails invoice={params.data?.invoice_recipient} confirmation={params.data.delivery_confirmation}>
          <Chip label={methodLabel(params.value, params.data?.invoice_recipient)} size="small" color={METHOD_COLORS[params.value] || 'default'} />
        </DeliveryDetails>
      ) : '—',
    },
    {
      field: 'status',
      headerName: t('sending_service.columns.status'),
      width: 185,
      cellRenderer: (params) => {
        const sentEmail = params.value === 'sent' && params.data?.delivery_confirmation?.event_type === 'SENT' && params.data?.delivery_confirmation?.actual_method === 'email';
        const progress = params.value === 'sent' && params.data?.delivery_confirmation?.event_type === 'SENT' ? params.data.delivery_confirmation.status_message : null;
        const label = progress ? t(progress.code, progress.params) : sentEmail ? t('invoices.state.sent_email') : t(`sending_service.statuses.${params.value}`, params.value);
        const color = params.data?.delivery_tracking_error ? 'warning' : STATUS_COLORS[params.value] || 'default';
        const chip = React.createElement(Chip, { label, size: 'small', color });
        if (params.data?.delivery_tracking_error) {
          return <DeliveryDetails invoice={params.data?.invoice_recipient} confirmation={params.data?.delivery_confirmation} trackingError={params.data.delivery_tracking_error}
            failureMessage={['failed', 'bounced', 'blocked', 'dropped', 'spam', 'deferred'].includes(params.value) ? getDeliveryErrorMessage(params.data, t) : undefined}>{chip}</DeliveryDetails>;
        }
        if (!['failed', 'bounced', 'blocked', 'dropped', 'spam', 'deferred'].includes(params.value)) {
          return <DeliveryDetails invoice={params.data?.invoice_recipient} confirmation={params.data?.delivery_confirmation} trackingError={params.data?.delivery_tracking_error}>{chip}</DeliveryDetails>;
        }
        const reason = getDeliveryErrorMessage(params.data, t);
        return React.createElement(Tooltip, { title: reason, arrow: true }, chip);
      },
    },
    {
      field: 'recipient',
      headerName: t('sending_service.columns.recipient'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'recipient_name',
      headerName: t('sending_service.columns.recipient_name'),
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'subject',
      headerName: t('sending_service.columns.subject'),
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'sender_name',
      headerName: t('sending_service.columns.sender'),
      width: 180,
    },
    {
      field: 'error_message',
      headerName: t('sending_service.columns.error'),
      width: 200,
      hide: true,
    },
    {
      field: 'message_id',
      headerName: t('sending_service.columns.message_id'),
      width: 200,
      hide: true,
    },
  ];

  return (
    <>
      <PageHeader />
      <PageContent>
        <ViiluDataGrid
          tenantId={tenantId}
          gridId="sending-service"
          rowData={rows}
          columnDefs={columnDefs}
          loading={loading}
          onRefresh={fetchData}
          onRowDoubleClicked={(event) => {
            const row = event.data;
            if (row.document_type === 'invoice' && row.document_id) {
              navigate(`/invoices/${row.document_id}`);
            }
          }}
        />
      </PageContent>
    </>
  );
}
